import React from 'react';
import { Card, Badge } from '../ui';
import { ArrowRight, AlertTriangle, CheckCircle, TrendingUp, TrendingDown } from 'lucide-react';

const JourneyComparison = ({ journey, comparison, loading = false }) => {
  if (loading) {
    return (
      <Card className="p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="space-y-3">
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
        </div>
      </Card>
    );
  }

  if (!journey || !comparison) {
    return (
      <Card className="p-6">
        <p className="text-gray-500 text-sm">No comparison data available</p>
      </Card>
    );
  }

  const getPerformanceStatus = (actual, avg) => {
    const diff = ((actual - avg) / avg) * 100;
    if (Math.abs(diff) < 10) return { status: 'normal', color: 'green', icon: CheckCircle };
    if (diff > 0) return { status: 'above', color: 'red', icon: TrendingUp };
    return { status: 'below', color: 'blue', icon: TrendingDown };
  };

  const formatDifference = (actual, avg, metric) => {
    if (!avg || !actual) return 'N/A';
    const diff = ((actual - avg) / avg) * 100;
    const sign = diff > 0 ? '+' : '';

    switch (metric) {
      case 'duration':
        return `${sign}${diff.toFixed(1)}% (${sign}${(actual - avg).toFixed(1)} hrs)`;
      case 'distance':
        return `${sign}${diff.toFixed(1)}% (${sign}${Math.round(actual - avg)} km)`;
      case 'fuel':
        return `${sign}${diff.toFixed(1)}% (${sign}${(actual - avg).toFixed(1)} L)`;
      default:
        return `${sign}${diff.toFixed(1)}%`;
    }
  };

  const metrics = [
    {
      label: 'Journey Duration',
      actual: journey.journey_duration_hours,
      avg: comparison.avg_duration_hours,
      metric: 'duration',
      unit: 'hrs',
    },
    {
      label: 'Distance Covered',
      actual: journey.fasttag_calculated_distance_km,
      avg: comparison.avg_distance_km,
      metric: 'distance',
      unit: 'km',
    },
    {
      label: 'Fuel Consumption',
      actual: journey.estimated_fuel_consumed_liters,
      avg: comparison.avg_fuel_liters,
      metric: 'fuel',
      unit: 'L',
    },
  ];

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Journey vs Average Performance</h3>
        <Badge variant="outline" className="text-xs">
          Route: {journey.route_signature?.substring(0, 8)}...
        </Badge>
      </div>

      <div className="space-y-4">
        {metrics.map((item) => {
          const performance = getPerformanceStatus(item.actual, item.avg);
          const PerformanceIcon = performance.icon;

          return (
            <div key={item.label} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">{item.label}</span>
                <PerformanceIcon className={`w-4 h-4 text-${performance.color}-600`} />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-1">This Journey</p>
                  <p className="text-lg font-bold text-gray-900">
                    {item.actual?.toFixed(item.metric === 'distance' ? 0 : 1) || 'N/A'} {item.unit}
                  </p>
                </div>

                <ArrowRight className="w-5 h-5 text-gray-400 mx-2" />

                <div className="flex-1 text-right">
                  <p className="text-xs text-gray-500 mb-1">Route Average</p>
                  <p className="text-lg font-bold text-gray-900">
                    {item.avg?.toFixed(item.metric === 'distance' ? 0 : 1) || 'N/A'} {item.unit}
                  </p>
                </div>
              </div>

              <div className="mt-3 pt-3 border-t border-gray-300">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Difference:</span>
                  <span className={`text-xs font-semibold text-${performance.color}-600`}>
                    {formatDifference(item.actual, item.avg, item.metric)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {comparison.total_journeys && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between text-xs text-gray-600">
            <span>Historical Data</span>
            <span className="font-semibold">{comparison.total_journeys} similar journeys</span>
          </div>
        </div>
      )}

      {journey.processing_status === 'processed' && (
        <div className="mt-4">
          <Badge variant="success" size="sm" className="w-full justify-center">
            <CheckCircle className="w-3 h-3 mr-1" />
            Analytics Processed
          </Badge>
        </div>
      )}

      {journey.processing_status === 'pending' && (
        <div className="mt-4">
          <Badge variant="warning" size="sm" className="w-full justify-center">
            <AlertTriangle className="w-3 h-3 mr-1" />
            Analytics Pending
          </Badge>
        </div>
      )}
    </Card>
  );
};

export default JourneyComparison;
